import { useContext, useMemo } from "react";
import { Link } from "react-router-dom";
import clsx from "clsx";
import AppContext from "../services/context.js";
import { trackEvent } from "../lib/analytics.js";
import SanitizedHtml from "./SanitizedHtml.jsx";

const RESULT_ORDER = ["Espécie", "Metabólito Funcional", "Efeito Terapêutico"];
const THERAPEUTIC_FALLBACK_PREFIX = "Efeitos:";

const SearchResult = () => {
  const {
    searchResults,
    setSearchTerm,
    setSearchResults,
    themeKey,
    activeFilters,
  } = useContext(AppContext);
  const isDarkTheme = themeKey === "dark";
  const selectedTherapeuticEffects = activeFilters?.therapeuticEffects || [];

  const groupedResults = useMemo(() => {
    const groups = searchResults.reduce((acc, item) => {
      if (!item?.type) {
        return acc;
      }
      if (!acc[item.type]) {
        acc[item.type] = [];
      }
      acc[item.type].push(item);
      return acc;
    }, {});

    return RESULT_ORDER.map((type) => ({
      type,
      items: groups[type] || [],
    })).filter((group) => group.items.length > 0);
  }, [searchResults]);

  const isShowingTherapeuticFallback = useMemo(() => {
    if (selectedTherapeuticEffects.length < 2) {
      return false;
    }

    return searchResults.some((item) => (
      item?.type === "Espécie" &&
      item?.reason?.startsWith(THERAPEUTIC_FALLBACK_PREFIX)
    ));
  }, [searchResults, selectedTherapeuticEffects.length]);

  const handleItemClick = (q) => {
    const [type, id] = q.split(":");
    return `/${type}/${id}`;
  };

  return (
    <article>
      {isShowingTherapeuticFallback && (
        <section className="px-4 pt-8 max-w-[900px] mx-auto">
          <div
            className={clsx(
              "rounded-lg border px-4 py-3 text-sm",
              isDarkTheme
                ? "border-greenbook-dark-primary bg-greenbook-dark-bg text-greenbook-dark-text"
                : "border-greenbook-light-primary bg-white/70 text-greenbook-light-text"
            )}
          >
            <p className="font-semibold">
              Nenhuma espécie foi encontrada com todos estes efeitos terapêuticos ao mesmo tempo:
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {selectedTherapeuticEffects.map((effect) => (
                <span
                  key={effect.id}
                  className={clsx(
                    "rounded px-2 py-1 text-xs font-medium",
                    isDarkTheme
                      ? "bg-greenbook-dark-green text-white"
                      : "bg-greenbook-light-green text-white"
                  )}
                >
                  {effect.label}
                </span>
              ))}
            </div>
            <p className="mt-3 opacity-80">
              Seguem abaixo os achados parciais dentro dos filtros selecionados.
            </p>
          </div>
        </section>
      )}
      {groupedResults.map(({ type, items }) => (
        <section key={type} className="px-4 py-8 max-w-[900px] mx-auto">
          <header>
            <h1 className="font-montserrat block my-4 text-3xl">{type}</h1>
          </header>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {items.map((item) => (
              <Link to={handleItemClick(item.q)} key={item.q} className="w-full flex">
                <button
                  className={clsx(
                    "p-4 box-border shadow-[0_0_0_4px_rgba(0,0,0,0.25)]",
                    "rounded-[20px] cursor-pointer w-full text-left flex flex-col justify-start",
                    "hover:shadow-[0_0_0_4px_rgba(0,0,0,1)]",
                    isDarkTheme
                      ? [
                          "bg-greenbook-dark-bg text-greenbook-dark-text border border-greenbook-dark-primary",
                          "hover:bg-greenbook-dark-primary hover:text-white",
                        ]
                      : [
                          "bg-greenbook-light-bg text-greenbook-light-text border border-gray-300",
                          "hover:bg-green-700 hover:text-white",
                        ]
                  )}
                  onClick={() => {
                    trackEvent('select_content', {
                      content_id: item.q,
                      content_type: item.type,
                      source: item.sourceLabel ? 'semantic_search_results' : 'search_results',
                    });
                    setSearchTerm("");
                    setSearchResults([]);
                  }}
                >
                  <span className="block font-semibold text-lg">{item.title}</span>
                  <span
                    className={clsx(
                      "block text-sm uppercase tracking-wide mt-1",
                      isDarkTheme ? "text-greenbook-dark-primary" : "text-greenbook-light-primary"
                    )}
                  >
                    {item.type}
                  </span>
                  {item.snippet && (
                    <blockquote className="mt-2 text-sm italic break-words opacity-90">
                      <SanitizedHtml
                        html={item.snippet}
                        className="space-y-1 [&_h1]:font-semibold [&_h2]:font-semibold [&_h3]:font-semibold [&_li]:ml-4 [&_li]:list-disc"
                      />
                    </blockquote>
                  )}
                  {(item.score != null || item.sourceLabel) && (
                    <span className="block text-xs mt-1 opacity-75">
                      {item.score != null && `Relevância: ${item.score.toFixed(2)}`}
                      {item.score != null && item.sourceLabel && ' · '}
                      {item.sourceLabel && `Fonte: ${item.sourceLabel}`}
                    </span>
                  )}
                  {item.reason && !item.snippet && (
                    <span className="block text-sm mt-2 break-words">{item.reason}</span>
                  )}
                </button>
              </Link>
            ))}
          </div>
        </section>
      ))}
    </article>
  );
};

export default SearchResult;
